import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultMST4030_R01DetailDto, defaultMST4030_R01HeaderDto, MST4030_R01DetailDto, MST4030_R01HeaderDto } from './Dto_MST4030_R01';

/**
 * MST4030_R01 필터 인터페이스
 */
export interface MST4030_R01Filter {
	compCd: string;
	workplaceCd: string;
	equipmentType: string;
	equipmentCd: string;
	equipmentNm: string;
	eqmStatus: string;				// 설비상태 (EQMSTATUS)
	eqmCd: string;					// 디테일 조회용 설비코드
}

/**
 * MST4030_R01 스토어 인터페이스
 */
export interface Store_MST4030_R01 {
	// ====== 필터 ======
	MST4030_R01Filter: MST4030_R01Filter;
	MST4030_R01PastFilter: MST4030_R01Filter;
	setMST4030_R01Filter: (filter: Partial<MST4030_R01Filter>) => void;
	setMST4030_R01PastFilter: (filter: Partial<MST4030_R01Filter>) => void;

	// ====== 헤더 리스트 및 선택/편집 ======
	MST4030_R01List: MST4030_R01HeaderDto[];
	setMST4030_R01List: (list: MST4030_R01HeaderDto[]) => void;

	selectedMST4030_R01: MST4030_R01HeaderDto | undefined;
	setSelectedMST4030_R01: (data: MST4030_R01HeaderDto | undefined) => void;
	initSelectedMST4030_R01: () => void;

	editMST4030_R01: MST4030_R01HeaderDto;
	setEditMST4030_R01: (data: MST4030_R01HeaderDto) => void;

	// ====== 디테일 리스트 및 선택/편집 ======
	MST4030_R01DetailList: MST4030_R01DetailDto[];
	setMST4030_R01DetailList: (data: MST4030_R01DetailDto[]) => void;
	insertMST4030_R01DetailList: (data: MST4030_R01DetailDto[], index?: number) => void;
	removeMST4030_R01DetailList: (index: number) => void;
	updateMST4030_R01DetailList: (index: number, data: MST4030_R01DetailDto) => void;

	selectedMST4030_R01DetailList: string[];
	setSelectedMST4030_R01DetailList: (idList: string[]) => void;
	addSelectedMST4030_R01DetailList: (idList: string[]) => void;
	removeSelectedMST4030_R01DetailList: (idList?: string[]) => void;
	initSelectedMST4030_R01DetailList: () => void;

	deletedMST4030_R01DetailList: string[];
	setDeletedMST4030_R01DetailList: (idList: string[]) => void;
	addDeletedMST4030_R01DetailList: (idList: string[]) => void;
	removeDeletedMST4030_R01DetailList: (idList?: string[]) => void;

	editMST4030_R01Detail: MST4030_R01DetailDto | null;
	setEditMST4030_R01Detail: (editDetail: MST4030_R01DetailDto | null) => void;

	originalMST4030_R01DetailList: MST4030_R01DetailDto[];
	setOriginalMST4030_R01DetailList: (data: MST4030_R01DetailDto[]) => void;

	// ====== 컬럼 상태(그리드) ======
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;				setAllCount: (allCount: number) => void;
	detailAllCount: number;			setDetailAllCount: (allCount: number) => void;
	detailFetchTrigger: boolean;	setDetailFetchTrigger: (trigger: boolean) => void;

	// ====== GridCalendar 상태 ======
	MST4030_R01GridCalendarState: {
		pickerVisible: boolean;
		pickerPosition: { top: number; left: number };
		targetRowIndex: number | null;
		targetField: string | null;
		targetDate: Date | undefined;
	};
	setMST4030_R01GridCalendarState: (state: Partial<Store_MST4030_R01['MST4030_R01GridCalendarState']>) => void;
	initMST4030_R01GridCalendarState: () => void;

	// ====== 초기화 ======
	initData: () => void;
}

/**
 * 기본 필터 값
 */
export const defaultMST4030_R01Filter: MST4030_R01Filter = {
	compCd: '',
	workplaceCd: '',
	equipmentType: '',
	equipmentCd: '',
	equipmentNm: '',
	eqmStatus: '',
	eqmCd: ''
}

/**
 * MST4030_R01 Zustand 스토어
 */
export const useMST4030_R01Store = create<Store_MST4030_R01>((set) => ({
	// ====== 필터 ======
	MST4030_R01Filter: defaultMST4030_R01Filter,
	MST4030_R01PastFilter: defaultMST4030_R01Filter,
	setMST4030_R01Filter: (filter) => set((state) => ({
		MST4030_R01Filter: {
			...state.MST4030_R01Filter,
			...filter
		}
	})),
	setMST4030_R01PastFilter: (filter) => set((state) => ({
		MST4030_R01PastFilter: {
			...state.MST4030_R01PastFilter,
			...filter
		}
	})),

	// ====== 헤더 리스트 및 선택/편집 ======
	MST4030_R01List: [],
	setMST4030_R01List: (list: MST4030_R01HeaderDto[]) => set((state) => {
		let newList: MST4030_R01HeaderDto[] = [];
		let valueIndex = 1;
		list.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueIndex++,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueIndex - 1,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
		});
		return {
			...state,
			MST4030_R01List: newList
		}
	}),

	selectedMST4030_R01: undefined,
	setSelectedMST4030_R01: (data: MST4030_R01HeaderDto | undefined) => set((state) => {
		return {
			...state,
			selectedMST4030_R01: data,
			selectedMST4030_R01DetailList: [],
			deletedMST4030_R01DetailList: [],
			detailFetchTrigger: true,
		};
	}),
	initSelectedMST4030_R01: () => set({ 
		selectedMST4030_R01: undefined, 
		editMST4030_R01: { ...defaultMST4030_R01HeaderDto, valueIndex: 0 },
		MST4030_R01DetailList: [],
		detailFetchTrigger: false,
	}),

	editMST4030_R01: defaultMST4030_R01HeaderDto,
	setEditMST4030_R01: (data: MST4030_R01HeaderDto) => set((state) => {
		let editMST4030_R01 = defaultMST4030_R01HeaderDto;
		if (data) {
			editMST4030_R01 = {
				...data
			}
		}
		return { ...state, editMST4030_R01: editMST4030_R01 };
	}),

	// ====== 디테일 리스트 및 선택/편집 ======
	MST4030_R01DetailList: [],
	setMST4030_R01DetailList: (data: MST4030_R01DetailDto[]) => set((state) => {
		let newList: MST4030_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			MST4030_R01DetailList: newList
		}
	}),
	insertMST4030_R01DetailList: (data: MST4030_R01DetailDto[], index?: number) => set((state) => {
		let newDetailList = [...state.MST4030_R01DetailList];
		let maxValueIndex = Math.max(0, ...newDetailList.map(item => item.valueIndex || 0)) + 1;

		const newData = data.map(item => ({
			...defaultMST4030_R01DetailDto,
			...item,
			valueIndex: maxValueIndex++,
			orderIndex: maxValueIndex - 1,
			orderIndexSeq: 0
		}));

		if (index !== undefined) {
			newDetailList.splice(index, 0, ...newData);
		} else {
			newDetailList.push(...newData);
		}

		return {
			...state,
			MST4030_R01DetailList: newDetailList
		}
	}),
	removeMST4030_R01DetailList: (index: number) => set((state) => {
		const newDetailList = [...state.MST4030_R01DetailList].filter((item) => item.valueIndex !== index);
		return {
			...state,
			MST4030_R01DetailList: newDetailList
		}
	}),
	updateMST4030_R01DetailList: (valueIndex: number, data: MST4030_R01DetailDto) => set((state) => {
		const _detailList = state.MST4030_R01DetailList.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		return {
			...state,
			MST4030_R01DetailList: _detailList
		}
	}),

	selectedMST4030_R01DetailList: [],
	setSelectedMST4030_R01DetailList: (idList: string[]) => set((state) => {
		return {
			...state,
			selectedMST4030_R01DetailList: idList
		}
	}),
	addSelectedMST4030_R01DetailList: (idList: string[]) => set((state) => {
		const _selectedList = [...state.selectedMST4030_R01DetailList];
		if (idList.length > 0) {
			_selectedList.push(...idList);
		}
		return {
			...state,
			selectedMST4030_R01DetailList: _selectedList
		}
	}),
	removeSelectedMST4030_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		selectedMST4030_R01DetailList: [...state.selectedMST4030_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedMST4030_R01DetailList: () => set({ 
		selectedMST4030_R01DetailList: [], 
		editMST4030_R01Detail: defaultMST4030_R01DetailDto 
	}),

	deletedMST4030_R01DetailList: [],
	setDeletedMST4030_R01DetailList: (idList: string[]) => set((state) => {
		return {
			...state,
			deletedMST4030_R01DetailList: idList
		}
	}),
	addDeletedMST4030_R01DetailList: (idList: string[]) => set((state) => {
		const _deletedList = [...state.deletedMST4030_R01DetailList];

		for (const valueIndex of idList) {
			const isInserted = state.MST4030_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.MST4030_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if (!isInserted && !isModified) {
				_deletedList.push(valueIndex);
			}
		}

		return {
			...state,
			deletedMST4030_R01DetailList: _deletedList
		}
	}),
	removeDeletedMST4030_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		deletedMST4030_R01DetailList: [...state.deletedMST4030_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),

	editMST4030_R01Detail: defaultMST4030_R01DetailDto,
	setEditMST4030_R01Detail: (editDetail: MST4030_R01DetailDto | null) => set((state) => {
		return {
			...state,
			editMST4030_R01Detail: editDetail
		}
	}),

	originalMST4030_R01DetailList: [],
	setOriginalMST4030_R01DetailList: (data: MST4030_R01DetailDto[]) => set((state) => {
		let newList: MST4030_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({
				...defaultMST4030_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalMST4030_R01DetailList: newList
		}
	}),

	// ====== 컬럼 상태(그리드) ======
	columnState: null,
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	allCount: 0,						setAllCount: (allCount: number) => set({ allCount }),
	detailAllCount: 0,					setDetailAllCount: (allCount: number) => set({ detailAllCount: allCount }),
	detailFetchTrigger: false,			setDetailFetchTrigger: (trigger: boolean) => set({ detailFetchTrigger: trigger }),

	// ====== GridCalendar 상태 ======
	MST4030_R01GridCalendarState: {
		pickerVisible: false,
		pickerPosition: { top: 0, left: 0 },
		targetRowIndex: null,
		targetField: null,
		targetDate: undefined
	},
	setMST4030_R01GridCalendarState: (newState: Partial<Store_MST4030_R01['MST4030_R01GridCalendarState']>) => set((state) => ({
		...state,
		MST4030_R01GridCalendarState: { ...state.MST4030_R01GridCalendarState, ...newState }
	})),
	initMST4030_R01GridCalendarState: () => set((state) => ({
		...state,
		MST4030_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),

	// ====== 초기화 ======
	initData: () => set((state) => ({
		...state,
		MST4030_R01Filter: defaultMST4030_R01Filter,
		MST4030_R01PastFilter: { ...defaultMST4030_R01Filter },
		MST4030_R01List: [],
		selectedMST4030_R01: undefined,
		editMST4030_R01: defaultMST4030_R01HeaderDto,
		MST4030_R01DetailList: [],
		deletedMST4030_R01DetailList: [],
		selectedMST4030_R01DetailList: [],
		editMST4030_R01Detail: defaultMST4030_R01DetailDto,
		allCount: 0,
		detailAllCount: 0,
		detailFetchTrigger: false,
		scrollTop: null,
		detailScrollTop: null,
		MST4030_R01GridCalendarState: {
			pickerVisible: false,
			pickerPosition: { top: 0, left: 0 },
			targetRowIndex: null,
			targetField: null,
			targetDate: undefined
		}
	})),
}));

